import { chatSidebarOpenAtom } from '@/atoms/chatSidebarAtoms';
import { setSidebarOpenTab } from '@/atoms/persistedPreferencesAtoms';
import { useMobilePanel } from '@/components/layout/Navigation/mobile';
import { NavItem } from '@/components/layout/Navigation/sidebar/nav-item';
import {
  useIsAgentHomeV1Enabled,
  useIsEvidenceSidePanelDisabledInEhrLaunch,
  useIsProjectsEnabled,
} from '@/features/flags';
import { IS_SIDEBAR_PANEL_ENABLED } from '@/features/flags/legacy-flags';
import { useManualExposure } from '@/hooks/amplitude';
import { useIsEhrLaunch } from '@/hooks/integrations/useIsEhrLaunch';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { getBaseChatPath } from '@heidi/agents/contracts';
import { useCanUseWorkHome, useResetProjectsComposerContext } from '@heidi/agents/data';
import { Evidence } from '@heidi/design-system/icons';
import { useTranslations } from '@heidi/runtime/i18n';
import { useSetAtom } from 'jotai';
import { Tv2 } from 'lucide-react';
import { useRouter } from 'next/router';
import { useCallback, useTransition } from 'react';

const EVIDENCE_ACTIVE_HREFS = [
  '/scribe/chat',
  '/scribe/chat/projects',
  '/library',
  '/scribe/calculators',
  '/evidence/cpd',
] as const;

export const EvidenceNavItem = () => {
  const { t } = useTranslations();
  const router = useRouter();
  const [, startTransition] = useTransition();
  const {
    isSessionlessEvidenceShown: enableSessionlessEvidence,
    isNhsUser,
    showManagedTeamActivationRequest,
  } = useEvidenceAccess();
  const isSidebarPanelEnabled = IS_SIDEBAR_PANEL_ENABLED();
  const isProjects = useIsProjectsEnabled();
  const isAgentHomeV1Enabled = useIsAgentHomeV1Enabled();
  const showWorkNav = useCanUseWorkHome();
  const href = getBaseChatPath(showWorkNav);

  // Hide the Evidence nav entry in EHR Launch when the side-panel disable flag is on.
  const isEhrLaunch = useIsEhrLaunch();
  const isEvidenceDisabledInEhrLaunch = useIsEvidenceSidePanelDisabledInEhrLaunch();

  useManualExposure({
    flagKey: 'release-sessionless-evidence',
    isEnabled: enableSessionlessEvidence,
    isVisible: true,
  });
  const setChatSidebarOpen = useSetAtom(chatSidebarOpenAtom);
  const { dismissMobileSidebar } = useMobilePanel();
  const resetProjectsComposerContext = useResetProjectsComposerContext();

  // True on the Work home only — the projects route with no chat id, across both
  // the current ``/[id]`` shape and the coming ``/[[...id]]`` catch-all.
  const isProjectsHome =
    (router.pathname === '/scribe/chat/projects' ||
      router.pathname.startsWith('/scribe/chat/projects/')) &&
    !router.query.id?.length;

  const onClick = useCallback(() => {
    // if the user needds to request access to evidence, don't open the sidebar
    if (showManagedTeamActivationRequest) return;
    if (isProjects && isProjectsHome) {
      resetProjectsComposerContext();
    }
    startTransition(() => {
      setSidebarOpenTab(undefined);
      // Only open old ChatSidebar when new SidebarPanel is disabled
      // When enabled, evidence content is shown in the new panel instead
      if (!isSidebarPanelEnabled) {
        setChatSidebarOpen(true);
      }
    });
    dismissMobileSidebar();
  }, [
    setChatSidebarOpen,
    dismissMobileSidebar,
    startTransition,
    isSidebarPanelEnabled,
    showManagedTeamActivationRequest,
    isProjects,
    isProjectsHome,
    resetProjectsComposerContext,
  ]);

  // ``Work`` is hardcoded — Crowdin key sync is out of scope for this change.
  const label = showWorkNav ? 'Work' : t('sidenav.evidence');
  const icon = showWorkNav ? <Tv2 className="size-5" /> : <Evidence />;

  // Agent Home replaces the legacy Work destination. Keep this guard at the
  // item boundary as well as SideNav so no alternate shell can mount a second
  // Work entry and reopen the old chat sidebar.
  if (isAgentHomeV1Enabled || (isEhrLaunch && isEvidenceDisabledInEhrLaunch)) {
    return null;
  }

  if (
    !enableSessionlessEvidence &&
    !isNhsUser &&
    !showManagedTeamActivationRequest &&
    !showWorkNav
  ) {
    return null;
  }

  return (
    <NavItem
      label={label}
      icon={icon}
      href={href}
      activeHrefs={EVIDENCE_ACTIVE_HREFS}
      onClick={onClick}
      testId="sidebar-link-view-evidence"
      sidebarPanelType="evidence"
    />
  );
};
